import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";

const LINKS = [
  { label: "How it works", href: "/#how-it-works" },
  { label: "The 22p model", href: "/#model" },
  { label: "Coverage", href: "/#coverage" },
  { label: "Objections solved", href: "/#objections" },
  { label: "Bean Ladder", href: "/ladder", route: true },
  { label: "FAQ", href: "/#faq" },
  { label: "About", href: "/#about" },
];

export const Nav = ({ dark = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const base = dark && !scrolled ? "text-[#FAF9F6]" : "text-[#0B1E17]";

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.215, 0.61, 0.355, 1] }}
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#F7F5F0]/85 backdrop-blur-xl border-b border-[#1B5E46]/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Logo dark={dark && !scrolled} />
        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) =>
            l.route ? (
              <Link key={l.label} to={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-semibold ${base} hover:text-[#D97706] transition-colors duration-300`}>
                {l.label}
              </Link>
            ) : (
              <a key={l.label} href={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-semibold ${base} hover:text-[#D97706] transition-colors duration-300`}>
                {l.label}
              </a>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono-brand font-semibold px-3 py-1.5 rounded-full border border-[#D97706]/40 text-[#D97706]">
            22p / cup donated
          </span>
          <a href="/#enquire" data-testid="nav-get-machine-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-[#0F2E23] text-[#F7F5F0] text-sm font-bold px-5 py-2.5 hover:bg-[#1B5E46] transition-colors duration-300">
            Get a Machine
          </a>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Menu"
            className={`lg:hidden p-2 ${base}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-[#F7F5F0] border-t border-[#1B5E46]/10 px-6 py-5 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <a key={l.label} href={l.href} onClick={() => setOpen(false)}
              className="text-base font-semibold text-[#0B1E17]">
              {l.label}
            </a>
          ))}
          <a href="/#enquire" onClick={() => setOpen(false)}
            className="inline-flex w-fit items-center rounded-full bg-[#0F2E23] text-[#F7F5F0] text-sm font-bold px-5 py-2.5">
            Get a Machine
          </a>
        </div>
      )}
    </motion.header>
  );
};
